import axios from 'axios';
import apiService from './apiService';

const API_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8002";
console.log('API URL:', API_URL); // Log the API URL to verify it's set correctly

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log('Making request to:', config.url, 'with data:', config.data);
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor: invalid auth token -> login
api.interceptors.response.use(
  (response) => {
    console.log('Response received:', response.data);
    return response;
  },
  (error) => {
    console.error('Response error:', error.response || error);
    const data = error.response?.data;
    if (data && apiService.isInvalidTokenPayload(data)) {
      apiService.clearSessionAndGoLogin();
    }
    return Promise.reject(error);
  }
);

class AuthService {
  async register(email, name, password, passwordConfirm, wantsNewsletter = false, termsAccepted = true) {
    try {
      console.log('Registering user with:', { email, name, passwordConfirm: '***' });
      const response = await api.post('/api/auth/register/', {
        email,
        name,
        password,
        password_confirm: passwordConfirm,
        wants_newsletter: !!wantsNewsletter,
        terms_accepted: !!termsAccepted,
      });
      console.log('Registration response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Registration error in service:', error);
      if (error.response) {
        // Handle specific error cases
        switch (error.response.status) {
          case 400:
            // Preserve axios error so callers can map DRF field / non_field_errors in the UI
            console.error('Validation error:', error.response.data);
            throw error;
          case 500:
            console.error('Server error:', error.response.data);
            throw new Error('Server error. Please try again later.');
          default:
            console.error('Unexpected error:', error.response.data);
            throw new Error('Registration failed. Please try again.');
        }
      }
      console.error('Network error:', error);
      throw new Error('Network error. Please check your connection.');
    }
  }

  async verifyEmail(uid, token) {
    try {
      const response = await api.post('/api/auth/verify-email/', { uid, token });
      return response.data;
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            throw new Error('Invalid verification token');
          case 500:
            throw new Error('Server error. Please try again later.');
          default:
            throw new Error('Verification failed. Please try again.');
        }
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  async requestPasswordReset(email) {
    // Backend always returns 200 (account-enumeration safe). Just propagate
    // any network/server failure.
    const response = await api.post('/api/auth/password-reset/', { email });
    return response.data;
  }

  async confirmPasswordReset(uid, token, newPassword, newPasswordConfirm) {
    try {
      const response = await api.post('/api/auth/password-reset-confirm/', {
        uid,
        token,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
      return response.data;
    } catch (error) {
      // Surface the original error so the form can render field-level
      // messages from DRF (e.g. validate_password output).
      throw error;
    }
  }

  async resendVerificationEmail(email) {
    try {
      const response = await api.post('/api/auth/resend-verification/', { email });
      return response.data;
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            throw new Error(error.response.data.detail || 'Email already verified or invalid');
          case 404:
            throw new Error('No unverified account found with this email');
          case 500:
            throw new Error('Server error. Please try again later.');
          default:
            throw new Error('Failed to resend email. Please try again.');
        }
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  async login(email, password) {
    try {
      const response = await api.post('/api-token-auth/', {
        username: email, // API expects username field
        password
      });

      // Store token
      const { token } = response.data;
      localStorage.setItem('authToken', token);

      return response.data;
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            throw new Error('Invalid email or password');
          case 500:
            throw new Error('Server error. Please try again later.');
          default:
            throw new Error('Login failed. Please try again.');
        }
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  logout() {
    try {
      console.log("Logging out in authService");

      // Eliminar de localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentOrganizationId'); // Clear organization context

      // Eliminar cookies
      this.deleteCookie('user_session');
      this.deleteCookie('auth_token');

      return true;
    } catch (error) {
      console.error("Error during logout:", error);
      return false;
    }
  }

  // Guardar sesión en cookies y localStorage
  async saveSession(userData, token) {
    try {
      console.log("Saving session in authService:", { userData, token });

      // Guardar en localStorage (keep `token` in sync for apiService.getAuthHeaders)
      localStorage.setItem('authToken', token);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Guardar en cookies
      this.setCookie('user_session', encodeURIComponent(JSON.stringify(userData)), 7);
      this.setCookie('auth_token', token, 7);

      return true;
    } catch (error) {
      console.error("Error saving session:", error);
      return false;
    }
  }

  // Helper method to get current user from cookies or localStorage
  getCurrentUser() {
    try {
      // Intentar primero desde cookies
      const userCookie = this.getCookie('user_session');
      if (userCookie) {
        console.log("User found in cookies");
        return JSON.parse(decodeURIComponent(userCookie));
      }

      // Si no hay en cookies, intentar localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        console.log("User found in localStorage");

        // Guardar también en cookies para la próxima vez
        const userData = JSON.parse(userStr);
        this.setCookie('user_session', encodeURIComponent(userStr), 7);

        return userData;
      }

      console.log("No user found");
      return null;
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  }

  // Helper method to check if user is authenticated
  async isAuthenticated() {
    try {
      // Intentar primero desde cookies
      const tokenCookie = this.getCookie('auth_token');
      if (tokenCookie) {
        console.log("Token found in cookies");
        return true;
      }

      // Si no hay en cookies, intentar localStorage (same keys as apiService.getAuthHeaders)
      const token =
        localStorage.getItem('authToken') || localStorage.getItem('token');
      if (token) {
        console.log("Token found in localStorage");

        this.setCookie('auth_token', token, 7);

        return true;
      }

      console.log("No token found");
      return false;
    } catch (error) {
      console.error("Error checking authentication:", error);
      return false;
    }
  }

  // Funciones para manejar cookies
  setCookie(name, value, days = 7) {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      return true;
    } catch (error) {
      console.error("Error setting cookie:", error);
      return false;
    }
  }

  getCookie(name) {
    try {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    } catch (error) {
      console.error("Error getting cookie:", error);
      return null;
    }
  }

  deleteCookie(name) {
    try {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      return true;
    } catch (error) {
      console.error("Error deleting cookie:", error);
      return false;
    }
  }
}

const authService = new AuthService();
export default authService; 